import {
  EducatorOpenAPI,
  setEducatorApiBaseForSSR,
  tryCatch,
} from '@educator/libs/educatorApi'
import {
  EducatorProfileService,
  type EducatorProfileResponse,
} from '@mc/educator-api'
import { removeQueryParams } from '@mc/next-common'
import { reportError } from '@mc/next-newrelic'

/**
 * Server-side profile read that never throws: a failed fetch is reported and
 * resolves to `undefined`, so SSR guards can fail open instead of locking users
 * out on a transient API error.
 *
 * @param accessToken The learner's access token. It's set on the educator API
 *   client, which concurrent SSR requests share, right before the request.
 * @param sourcePage The page the profile is fetched for, reported without its
 *   query string when the fetch fails.
 */
export const safeGetEducatorProfile = async (
  accessToken: string,
  sourcePage?: string,
): Promise<EducatorProfileResponse | undefined> => {
  setEducatorApiBaseForSSR()
  EducatorOpenAPI.TOKEN = accessToken

  const response = await tryCatch(() =>
    EducatorProfileService.getEducatorProfile(),
  )

  if ('data' in response) return response.data

  reportError(response.error, {
    location: 'safeGetEducatorProfile',
    message: 'Failed to fetch educator profile',
    ...(sourcePage ? { sourcePage: removeQueryParams(sourcePage) } : {}),
  })

  return undefined
}
